import {codeMirrorPhrases} from "../codemirror_i18n.js"
/**
 * ToolEditor hook — CodeMirror 6 editor for Seiva Tool Builder.
 *
 * Uses phx-update="ignore" to protect CodeMirror DOM.
 * CodeMirror is bundled via npm — no CDN race conditions.
 * Supports lazy init: if container starts at w-0 (hidden),
 * a ResizeObserver waits until it becomes visible to create the editor.
 *
 * Server events:
 *   set_editor_code     — push new code content
 *   set_editor_language  — switch syntax highlighting
 *
 * Client events:
 *   code_changed         — user edited code, sends { value }
 */

import { EditorView, basicSetup } from "codemirror"
import { EditorState } from "@codemirror/state"
import { javascript } from "@codemirror/lang-javascript"
import { python } from "@codemirror/lang-python"
import { go } from "@codemirror/lang-go"
import { json } from "@codemirror/lang-json"
import { css } from "@codemirror/lang-css"
import { html } from "@codemirror/lang-html"
import { oneDark } from "@codemirror/theme-one-dark"
import { keymap } from "@codemirror/view"
import { githubDark, githubLight } from "@uiw/codemirror-theme-github"
import { dracula } from "@uiw/codemirror-theme-dracula"
import { nord } from "@uiw/codemirror-theme-nord"

const THEMES = {
  "vs-dark": oneDark,
  "one-dark": oneDark,
  "github-dark": githubDark,
  "github-light": githubLight,
  "dracula": dracula,
  "nord": nord,
  "light": null,
}

const TAB_SIZE = { go: 4, python: 4, javascript: 2, json: 2 }

function langExt(lang) {
  switch (lang) {
    case "python": return python()
    case "go": return go()
    case "javascript": case "js": return javascript({ jsx: false })
    case "typescript": case "ts": return javascript({ jsx: false, typescript: true })
    case "typescriptreact": case "tsx": return javascript({ jsx: true, typescript: true })
    case "json": return json()
    case "css": return css()
    case "html": return html()
    default: return javascript()
  }
}

export default {
  mounted() {
    this._ignoreUpdate = false
    this._pendingCode = null
    this._pendingLang = null
    this._currentLang = this.el.dataset.language || "python"
    this._themeName = localStorage.getItem("seiva_editor_theme") || "dracula"

    // Register server event handlers immediately — they may fire before editor exists
    this.handleEvent("set_editor_code", ({ value }) => {
      if (this.view) {
        const current = this.view.state.doc.toString()
        if (current !== (value || "")) {
          this._ignoreUpdate = true
          this.view.dispatch({
            changes: { from: 0, to: current.length, insert: value || "" }
          })
          this._ignoreUpdate = false
        }
      } else {
        this._pendingCode = value
      }
    })

    this.handleEvent("set_editor_language", ({ language }) => {
      if (this.view && language) {
        this._currentLang = language
        const content = this.view.state.doc.toString()
        this._ignoreUpdate = true
        this.view.setState(this._makeState(content, language))
        this._ignoreUpdate = false
      } else if (language) {
        this._pendingLang = language
      }
    })

    this.handleEvent("set_editor_theme", ({ theme }) => {
      this._themeName = theme
      localStorage.setItem("seiva_editor_theme", theme)
      if (this.view) {
        const content = this.view.state.doc.toString()
        this._ignoreUpdate = true
        this.view.setState(this._makeState(content, this._currentLang))
        this._ignoreUpdate = false
      }
    })

    // Try to create immediately; if container is hidden, observe until visible
    if (!this._tryInit()) {
      this._resizeObserver = new ResizeObserver(() => {
        if (this._tryInit() && this._resizeObserver) {
          this._resizeObserver.disconnect()
          this._resizeObserver = null
        }
      })
      this._resizeObserver.observe(this.el)
    }

  },

  _makeState(content, lang) {
    const baseTheme = EditorView.theme({
      "&": { height: "100%", minHeight: "0", width: "100%", fontSize: "13px" },
      ".cm-scroller": { height: "100%", overflow: "auto", fontFamily: "'JetBrains Mono', 'Fira Code', monospace" },
      ".cm-content": { padding: "8px 0" },
    })

    const extensions = [
      basicSetup,
      EditorState.phrases.of(codeMirrorPhrases()),
      langExt(lang || this._currentLang),
      baseTheme,
      EditorState.tabSize.of(TAB_SIZE[lang || this._currentLang] || 2),
      keymap.of([{
        key: "Mod-s",
        run: () => { this.pushEvent("editor_save_tool", {}); return true }
      }]),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !this._ignoreUpdate) {
          this.pushEvent("code_changed", { value: update.state.doc.toString() })
        }
      }),
    ]
    const themeExt = THEMES[this._themeName]
    if (themeExt) extensions.push(themeExt)
    return EditorState.create({ doc: content || "", extensions })
  },

  /**
   * Creates the editor if the container has real dimensions.
   * Returns true if initialized, false if container is still hidden.
   */
  _tryInit() {
    if (this.view) return true
    if (this.el.offsetWidth < 10 || this.el.offsetHeight < 10) return false

    const lang = this._pendingLang || this._currentLang
    const code = this._pendingCode || this.el.dataset.value || ""
    this._currentLang = lang

    this.view = new EditorView({
      state: this._makeState(code, lang),
      parent: this.el,
    })

    // UX refinement 2026-07: a server-rendered shimmer sits above the editor
    // until CodeMirror is actually on screen. No-op when the overlay isn't
    // rendered (e.g. HTTP builder has no CodeMirror).
    document.getElementById("tool-editor-cm-skeleton")?.remove()

    this._pendingCode = null
    this._pendingLang = null
    return true
  },

  // With phx-update="ignore", updated() is NOT called — that's the point.
  // All communication happens via handleEvent.

  destroyed() {
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this.view) this.view.destroy()
  }
}
